/**
 * VetrinaAgente — pagina di configurazione del profilo pubblico `/agente/:slug`.
 *
 * Feature riservata al piano PRO (gating fatto dal backend con 402; qui
 * mostriamo un paywall amichevole se l'utente non è Pro).
 *
 * Composizione:
 *   1. Hero · introduzione, link condivisibile + copia
 *   2. Slug · input con check live di disponibilità
 *   3. Bio · 320 char
 *   4. Stile · 4 layout (Elegante / Lussuoso / Moderno / Minimal)
 *   5. Colore brand · BrandColorPicker (15 palette + HEX custom)
 *   6. Visibilità · toggle "Pagina pubblica online"
 *   7. Immobili in vetrina · lista con toggle per singolo immobile
 *
 * iter v32 r37 — User M1428.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  ArrowRight, Copy, Eye, EyeSlash, Globe, House, Lock, SealCheck,
  CheckCircle, WarningCircle, ArrowSquareOut, Lightbulb, ShareNetwork,
  LinkSimple, PaintBrush, TextAa,
} from "@phosphor-icons/react";
import BrandColorPicker from "@/components/BrandColorPicker";
import { Switch } from "@/components/ui/switch";
import { isReaderMode } from "@/lib/iosReader";
import SectionCoachmark from "@/components/SectionCoachmark";

const STYLES = [
  { id: "elegante", name: "Elegante", desc: "Bodoni italic, magazine cover.", swatch: "#F1F4ED" },
  { id: "luxury",   name: "Lussuoso", desc: "Spectral, raffinato, centrato.",  swatch: "#F5F0E6" },
  { id: "moderno",  name: "Moderno",  desc: "Archivo bold, statement.",        swatch: "#1C1C1A" },
  { id: "minimal",  name: "Minimal",  desc: "Space Grotesk, geometrico.",      swatch: "#FAFAF7" },
];

const BIO_MAX = 320;

export default function VetrinaAgente() {
  const { user } = useAuth();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  // form state
  const [slug, setSlug] = useState("");
  const [bio, setBio] = useState("");
  const [agencyName, setAgencyName] = useState("");
  const [style, setStyle] = useState("elegante");
  const [color, setColor] = useState("");
  const [visible, setVisible] = useState(true);
  // ux state
  const [slugCheck, setSlugCheck] = useState({ status: "idle", reason: "" });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  // properties
  const [props, setProps] = useState([]);

  // Load profile + properties in parallel
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/me/agent-page").catch((e) => ({ error: e })),
      api.get("/properties").catch(() => ({ data: [] })),
    ]).then(([profileRes, propsRes]) => {
      if (cancelled) return;
      const p = profileRes?.data || profileRes; // axios shape
      if (profileRes?.error || !p) {
        toast.error("Errore caricamento profilo");
        setLoading(false);
        return;
      }
      setData(p);
      setSlug(p.public_slug || p.suggested_slug || "");
      setBio(p.bio || "");
      setAgencyName(p.agency_name || "");
      setStyle(p.profile_style || "elegante");
      setColor(p.brand_color || "");
      setVisible(p.public_visible !== false);
      const propsList = (propsRes?.data || []).filter(
        (x) => x && !x.is_deleted && (x.status !== "archived")
      );
      setProps(propsList);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const isPro = !!data?.is_pro;
  // iter v32 r37q · User M1447 — Per il display, mostriamo sempre il dominio
  // canonico prontoimmo.it (anche se siamo su preview). Per copy/share/open
  // usiamo invece l'origin reale così tutto funziona anche durante lo sviluppo.
  const PROD_HOST = "https://prontoimmo.it";
  const liveUrl = useMemo(() => {
    if (!slug || !slugLooksValid(slug)) return "";
    const origin = (typeof window !== "undefined") ? window.location.origin : PROD_HOST;
    return `${origin}/agente/${normalizeSlug(slug)}`;
  }, [slug]);
  // iter v32 r48 · User M1445 — Link "smart share" per WhatsApp/Telegram/IG.
  // Quando l'utente condivide il proprio sito, deve passare per
  // `/api/share/agente/{slug}` così i bot (WhatsApp ecc.) ricevono OG meta
  // tag dedicati con cover dinamica (foto agente + nome). I browser umani
  // sono redirezionati a /agente/{slug}. Risolve il problema della preview
  // duplicata/mancante segnalato dall'utente.
  const shareUrl = useMemo(() => {
    if (!slug || !slugLooksValid(slug)) return "";
    const origin = (typeof window !== "undefined") ? window.location.origin : PROD_HOST;
    return `${origin}/api/share/agente/${normalizeSlug(slug)}`;
  }, [slug]);
  const publicUrlDisplay = useMemo(() => {
    if (!slug || !slugLooksValid(slug)) return "";
    return `prontoimmo.it/agente/${normalizeSlug(slug)}`;
  }, [slug]);

  // Slug live availability check (debounced)
  useEffect(() => {
    if (!isPro) return;
    const candidate = normalizeSlug(slug);
    if (!candidate || candidate === (data?.public_slug || "")) {
      setSlugCheck({ status: "idle", reason: "" });
      return;
    }
    setSlugCheck({ status: "checking", reason: "" });
    const t = setTimeout(() => {
      api.get(`/me/agent-page/slug-check`, { params: { slug: candidate } })
        .then((r) => {
          const { available, reason } = r.data || {};
          setSlugCheck({ status: available ? "ok" : "taken", reason: reason || "" });
        })
        .catch(() => setSlugCheck({ status: "idle", reason: "" }));
    }, 400);
    return () => clearTimeout(t);
  }, [slug, isPro, data?.public_slug]);

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      // iter v32 r48 · Copia il link share-friendly (`/api/share/agente/…`)
      // così WhatsApp & co. mostrano la card branded con foto/nome agente
      // invece dell'anteprima generica della SPA.
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copiato");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  const save = async () => {
    if (!isPro) return;
    if (slugCheck.status === "taken") {
      toast.error("Lo slug è già usato. Provane un altro.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        public_slug: normalizeSlug(slug),
        bio: bio || null,
        profile_style: style,
        brand_color: color || null,
        public_visible: visible,
        agency_name: agencyName,
      };
      await api.patch("/me/agent-page", payload);
      // refetch to be sure
      const r = await api.get("/me/agent-page");
      setData(r.data);
      toast.success("Salvato");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore nel salvataggio";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const toggleProperty = async (pid, next) => {
    if (!isPro) return;
    // optimistic
    setProps((arr) => arr.map((p) => (p.id === pid ? { ...p, is_public_on_agent_page: next } : p)));
    try {
      await api.patch(`/properties/${pid}/visibility`, { is_public_on_agent_page: next });
    } catch {
      // revert
      setProps((arr) => arr.map((p) => (p.id === pid ? { ...p, is_public_on_agent_page: !next } : p)));
      toast.error("Errore nell'aggiornare l'immobile");
    }
  };

  if (loading) {
    return (
      <AppShell title="Il tuo sito">
        <div className="px-5 py-12 text-sm text-[#6B6A65] animate-pulse" data-testid="vetrina-loading">
          Caricamento profilo…
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Il tuo sito">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-32 space-y-8 overflow-x-hidden" data-testid="vetrina-agente-page">
        {/* HEADER · iter v32 r37u — User M1453: "Il tuo sito personale".
            "L'unico in Italia" badge per chiarire la value prop. */}
        <header>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#1C1C1A] text-white text-[10px] uppercase tracking-[0.18em] font-semibold mb-3" data-testid="vetrina-unique-badge">
            <SealCheck size={11} weight="fill" />
            Unico in Italia · Incluso in Pro
          </div>
          <h1 className="font-editorial text-3xl sm:text-4xl text-[#1C1C1A] leading-tight">
            Il tuo sito <span className="italic text-[#5F6B53]">personale.</span>
          </h1>
          <p className="text-[14.5px] text-[#6B6A65] mt-2 leading-relaxed max-w-2xl">
            Un dominio al tuo nome con bio, contatti e immobili sempre aggiornati. Da mettere nella bio Instagram, in firma email, sui biglietti da visita.
          </p>
        </header>

        {/* PAYWALL · se non Pro */}
        {!isPro && (
          <PaywallCard isReaderMode={isReaderMode} />
        )}

        {/* iter v33 r8 · User M1485: «non è chiaro cosa uno può fare».
            Tutorial contestuale che appare UNA VOLTA SOLA, spiega
            in 3 mosse cosa può fare qui. */}
        {isPro && (
          <SectionCoachmark
            id="vetrina-agente"
            title="Il tuo sito personale in tre mosse"
            steps={[
              {
                icon: <LinkSimple size={15} weight="bold" />,
                title: "Scegli il tuo indirizzo",
                body: "Un URL corto tipo prontoimmo.it/agente/nome-cognome, da mettere ovunque.",
              },
              {
                icon: <TextAa size={15} weight="bold" />,
                title: "Personalizza stile e colore",
                body: "Bio, foto, 4 stili grafici e un colore brand che ti rappresenta.",
              },
              {
                icon: <ShareNetwork size={15} weight="bold" />,
                title: "Condividilo",
                body: "I tuoi immobili appaiono in automatico. Aggiorni una volta, li vedi tutti.",
              },
            ]}
          />
        )}

        {/* WELCOME CARD · prima volta che un PRO apre la pagina (slug ancora
            vuoto) — guida i 3 passi iniziali. iter v32 r37d · User M1429. */}
        {isPro && !data?.public_slug && (
          <FirstSetupGuide />
        )}

        {/* CONFIG (solo Pro) */}
        <fieldset
          disabled={!isPro}
          className={`min-w-0 w-full ${!isPro ? "opacity-60 pointer-events-none select-none" : ""}`}
          data-testid="vetrina-config"
        >
          {/* LINK CONDIVISIBILE · iter v32 r37q — User M1447: layout
              overflow su iPhone. URL ora `break-all` + container `overflow-hidden`
              + font ridotti su mobile. Bottoni in colonna su mobile (label corte). */}
          <Section title="Il tuo link" eyebrow="01">
            <div className="rounded-xl border border-[#E5E1D8] bg-white p-3.5 sm:p-4 overflow-hidden">
              <div className="flex items-center gap-2 mb-2">
                <SealCheck size={14} weight="fill" className="text-[#7C8B6E]" />
                <span className="text-[10.5px] uppercase tracking-[0.22em] text-[#6B6A65]">URL pubblico</span>
              </div>
              <div className="break-all">
                <code
                  className="text-[14px] sm:text-[16px] text-[#1C1C1A] font-mono leading-tight"
                  data-testid="vetrina-public-url"
                >
                  {publicUrlDisplay || "—"}
                </code>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-1.5">
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md border border-[#E5E1D8] bg-white hover:bg-[#F1F4ED] text-[#1C1C1A] text-[11.5px] font-semibold transition-colors disabled:opacity-50"
                  onClick={copyLink}
                  disabled={!liveUrl}
                  data-testid="vetrina-copy-link"
                >
                  {copied ? <CheckCircle size={12} weight="fill" className="text-[#7C8B6E]" /> : <Copy size={12} weight="regular" />}
                  {copied ? "Copiato" : "Copia"}
                </button>
                {liveUrl ? (
                  <a
                    href={liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md border border-[#E5E1D8] bg-white hover:bg-[#F1F4ED] text-[#1C1C1A] text-[11.5px] font-semibold transition-colors"
                    data-testid="vetrina-open-link"
                  >
                    Apri <ArrowSquareOut size={12} weight="regular" />
                  </a>
                ) : (
                  <span className="inline-flex items-center justify-center px-2 py-1.5 rounded-md border border-[#E5E1D8] bg-white text-[#6B6A65] text-[11.5px] opacity-50">Apri</span>
                )}
                {liveUrl ? (
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`Le lascio il mio sito personale con tutte le proposte immobiliari:\n${shareUrl || liveUrl}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-[#25D366] hover:bg-[#1FA855] text-white text-[11.5px] font-semibold transition-colors"
                    data-testid="vetrina-share-wa"
                    aria-label="Condividi su WhatsApp"
                  >
                    <ShareNetwork size={12} weight="bold" /> <span className="hidden sm:inline">WhatsApp</span><span className="sm:hidden">Invia</span>
                  </a>
                ) : (
                  <span className="inline-flex items-center justify-center px-2 py-1.5 rounded-md bg-[#25D366]/30 text-white text-[11.5px] opacity-50">Invia</span>
                )}
              </div>
            </div>
          </Section>

          {/* SLUG */}
          <Section title="L'indirizzo del tuo sito" eyebrow="02" hint="Lettere, numeri e trattini. Resta tuo per sempre.">
            <div className="rounded-xl border border-[#E5E1D8] bg-white p-4">
              <div className="flex items-center text-[14px]">
                <span className="text-[#6B6A65] mr-1">prontoimmo.it/agente/</span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="es. mario-rossi"
                  className="flex-1 min-w-0 px-2 py-1.5 border-b border-[#E5E1D8] focus:border-[#7C8B6E] focus:outline-none font-mono text-[14px]"
                  maxLength={40}
                  data-testid="vetrina-slug-input"
                />
              </div>
              <SlugStatus status={slugCheck.status} reason={slugCheck.reason} />
            </div>
          </Section>

          {/* BIO */}
          <Section title="La tua presentazione" eyebrow="03" hint="Chi sei. Dove operi. In 2-3 righe.">
            <div className="rounded-xl border border-[#E5E1D8] bg-white p-4">
              <textarea
                rows={4}
                value={bio}
                onChange={(e) => setBio(e.target.value.slice(0, BIO_MAX))}
                placeholder="Es. «Mi occupo di vendita e acquisto immobili a Milano da 12 anni. Mi piace ascoltare prima di proporre.»"
                className="w-full border border-[#E5E1D8] rounded-lg p-3 text-[14.5px] leading-relaxed focus:border-[#7C8B6E] focus:outline-none"
                data-testid="vetrina-bio-input"
              />
              <div className="mt-1 text-right text-[11px] text-[#6B6A65]">
                {bio.length}/{BIO_MAX}
              </div>
            </div>
          </Section>

          {/* AGENCY NAME · iter v32 r37s · User M1449 — opzionale, appare
              come sottotitolo sopra al nome sulla vetrina pubblica. Lasciato
              vuoto = "Agente · indipendente". */}
          <Section
            title="La tua agenzia"
            eyebrow="04"
            hint="Se lavori in agenzia o hai uno tuo, scrivilo. Apparirà come riga sopra il tuo nome. Lascia vuoto se preferisci «agente indipendente»."
          >
            <div className="rounded-xl border border-[#E5E1D8] bg-white p-4">
              <input
                type="text"
                value={agencyName}
                onChange={(e) => setAgencyName(e.target.value.slice(0, 80))}
                placeholder="Es. Immobiliare Baio · Via S. Tomaso 41"
                className="w-full border-b border-[#E5E1D8] focus:border-[#7C8B6E] focus:outline-none px-1 py-1.5 text-[14.5px]"
                maxLength={80}
                data-testid="vetrina-agency-input"
              />
              <div className="mt-1 text-right text-[11px] text-[#6B6A65]">
                {agencyName.length}/80
              </div>
            </div>
          </Section>

          {/* STILE */}
          <Section title="Lo stile della pagina" eyebrow="05" hint="Coordinato con i tuoi annunci.">
            <div className="grid grid-cols-2 gap-2.5 sm:gap-3" data-testid="vetrina-style-grid">
              {STYLES.map((s) => {
                const active = style === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStyle(s.id)}
                    className={`text-left rounded-xl border p-3 sm:p-4 transition-all min-w-0 ${
                      active
                        ? "border-[#7C8B6E] ring-1 ring-[#7C8B6E]/40 bg-white"
                        : "border-[#E5E1D8] bg-white hover:border-[#B8C0AA]"
                    }`}
                    data-testid={`vetrina-style-${s.id}`}
                  >
                    <div
                      className="w-full h-14 sm:h-16 rounded-md mb-2.5 flex items-end p-2"
                      style={{ background: s.swatch, border: "1px solid rgba(28,28,26,0.12)" }}
                    >
                      <span
                        className="text-[18px] leading-none"
                        style={{
                          fontFamily: s.id === "elegante" ? "'Bodoni Moda', serif"
                            : s.id === "luxury" ? "'Spectral', serif"
                            : s.id === "moderno" ? "'Archivo', sans-serif"
                            : "'Space Grotesk', sans-serif",
                          color: s.id === "moderno" ? "#FAFAF7" : "#1C1C1A",
                          fontStyle: (s.id === "elegante" || s.id === "luxury") ? "italic" : "normal",
                          fontWeight: s.id === "moderno" ? 900 : s.id === "luxury" ? 200 : 400,
                          textTransform: s.id === "moderno" ? "uppercase" : "none",
                          letterSpacing: s.id === "moderno" || s.id === "minimal" ? "-0.02em" : "0",
                        }}
                      >
                        Aa
                      </span>
                    </div>
                    <div className="font-editorial text-[15px] sm:text-[16px] text-[#1C1C1A] truncate">{s.name}</div>
                    <div className="text-[11.5px] sm:text-[12px] text-[#6B6A65] mt-0.5 line-clamp-2">{s.desc}</div>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* COLORE BRAND */}
          <Section
            title="Il tuo colore"
            eyebrow="06"
            hint="È il colore di accento usato per i prezzi, i titoli e le icone della tua vetrina pubblica. Scegli quello che ti rappresenta — i tuoi annunci si coordineranno in automatico."
          >
            <div className="rounded-xl border border-[#E5E1D8] bg-white p-4">
              <BrandColorPicker
                value={color}
                onChange={setColor}
                variant="grid"
              />
              {/* iter v32 r37s · Anteprima dal vivo: l'utente vede subito
                  dove e come quel colore comparirà sulla sua vetrina. */}
              <ColorPreview color={color || "#7C8B6E"} />
            </div>
          </Section>

          {/* VISIBILITÀ */}
          <Section title="Visibilità" eyebrow="07">
            <div className="rounded-xl border border-[#E5E1D8] bg-white p-4 flex items-start gap-3" data-testid="vetrina-visibility-row">
              <PillToggle
                checked={visible}
                onChange={setVisible}
                data-testid="vetrina-visibility-toggle"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[14px] text-[#1C1C1A] flex items-center gap-2">
                  {visible ? <Eye size={14} weight="regular" /> : <EyeSlash size={14} weight="regular" />}
                  Pagina pubblica {visible ? "online" : "nascosta"}
                </div>
                <div className="text-[12.5px] text-[#6B6A65] mt-0.5">
                  {visible
                    ? "Chi visita il link vede la tua vetrina."
                    : "Chi visita il link vede una pagina 404. Puoi riattivare in qualsiasi momento."}
                </div>
              </div>
            </div>
          </Section>

          {/* PROPERTIES */}
          <Section
            title={`I tuoi immobili in vetrina (${props.length})`}
            eyebrow="08"
            hint="Per default ogni immobile attivo compare nella tua vetrina. Disattiva quelli che vuoi tenere riservati."
          >
            {props.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[#E5E1D8] bg-white p-6 text-center text-[13px] text-[#6B6A65]" data-testid="vetrina-no-properties">
                <House size={22} weight="light" className="mx-auto mb-2 opacity-60" />
                Nessun immobile attivo. Quando ne creerai uno apparirà qui per essere mostrato o nascosto.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="vetrina-properties-list">
                {props.map((p) => {
                  const onShow = p.is_public_on_agent_page !== false;
                  return (
                    <li
                      key={p.id}
                      className="rounded-xl border border-[#E5E1D8] bg-white p-3 flex items-center gap-3"
                      data-testid={`vetrina-property-${p.id}`}
                    >
                      <div
                        className="w-12 h-12 rounded-md flex-shrink-0 bg-cover bg-center"
                        style={{ backgroundImage: `url('${p.cover_photo_url || ""}')`, background: p.cover_photo_url ? undefined : "#E5E1D8" }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[14px] text-[#1C1C1A] truncate">
                          {p.title || "(senza titolo)"}
                        </div>
                        <div className="text-[12px] text-[#6B6A65] truncate">
                          {[p.city, p.sqm && `${p.sqm} m²`, p.price && `€ ${new Intl.NumberFormat("it-IT").format(p.price)}`]
                            .filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <PillToggle
                        checked={onShow}
                        onChange={(v) => toggleProperty(p.id, v)}
                        data-testid={`vetrina-property-toggle-${p.id}`}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>

          {/* TIPS */}
          <div className="rounded-xl bg-[#F1F4ED] border border-[#A8B59A]/40 p-4 flex gap-3" data-testid="vetrina-tips">
            <Lightbulb size={18} weight="regular" className="text-[#7C8B6E] flex-shrink-0 mt-0.5" />
            <div className="text-[13px] text-[#1C1C1A] leading-relaxed">
              <strong>Dove metterlo?</strong>{" "}
              Bio Instagram, firma email, biglietti da visita, descrizione WhatsApp Business.
              Un solo link che racconta chi sei e mostra i tuoi immobili attivi — sempre aggiornato in automatico.
            </div>
          </div>
        </fieldset>

        {/* SAVE BAR */}
        {isPro && (
          <div className="sticky bottom-0 left-0 right-0 bg-[#FAFAF7]/90 backdrop-blur-sm border-t border-[#E5E1D8] py-3 -mx-4 sm:-mx-6 px-4 sm:px-6 mt-8" data-testid="vetrina-save-bar">
            <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
              <Link to="/dashboard/profile" className="text-[13px] text-[#6B6A65] underline">
                Torna al profilo
              </Link>
              <button
                type="button"
                onClick={save}
                disabled={saving || slugCheck.status === "taken"}
                className="btn-primary text-[14px]"
                data-testid="vetrina-save-btn"
              >
                {saving ? "Salvataggio…" : "Salva modifiche"}
                {!saving && <ArrowRight size={14} weight="bold" />}
              </button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

/* ────────── Sub-components ────────── */

/**
 * PillToggle — toggle a pillola affidabile, indipendente da Tailwind purge
 * o CSS globali. Inline styles puri così non rompe mai su iOS Safari.
 * iter v32 r37t · User M1450 — sostituisce shadcn Switch che renderizzava
 * deformato (cerchione gigante) sulla pagina pubblica produzione.
 * iter v32 r48 · User M1458 — proporzione più ovale (52×26 invece di 44×24)
 * coerente con i toggle di Profile.jsx ("Promemoria agenda", "Avvisi home").
 */
function PillToggle({ checked, onChange, "data-testid": dataTestid, color = "#7C8B6E" }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={dataTestid}
      style={{
        display: "inline-flex",
        alignItems: "center",
        boxSizing: "border-box",
        width: 52,
        height: 26,
        minWidth: 52,
        minHeight: 26,
        borderRadius: 9999,
        background: checked ? color : "#D9D6CE",
        position: "relative",
        flexShrink: 0,
        alignSelf: "center",
        cursor: "pointer",
        border: "none",
        padding: 0,
        transition: "background 0.18s ease-out",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: checked ? 28 : 3,
          width: 20,
          height: 20,
          borderRadius: 9999,
          background: "#FFFFFF",
          boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
          transition: "left 0.18s ease-out",
        }}
      />
    </button>
  );
}

function Section({ title, eyebrow, hint, children }) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-3">
        {eyebrow && (
          <span className="text-[10px] uppercase tracking-[0.32em] text-[#7C8B6E] font-mono">
            {eyebrow}
          </span>
        )}
        <h2 className="font-editorial text-[20px] text-[#1C1C1A]">{title}</h2>
      </div>
      {hint && <p className="text-[12.5px] text-[#6B6A65] leading-relaxed">{hint}</p>}
      <div className="pt-2">{children}</div>
    </section>
  );
}

function SlugStatus({ status, reason }) {
  if (status === "idle") return null;
  if (status === "checking") {
    return <div className="mt-2 text-[12px] text-[#6B6A65] flex items-center gap-1.5" data-testid="vetrina-slug-status-checking">verifico…</div>;
  }
  if (status === "ok") {
    return (
      <div className="mt-2 text-[12px] text-[#5B7A4A] flex items-center gap-1.5" data-testid="vetrina-slug-status-ok">
        <CheckCircle size={12} weight="fill" /> Disponibile
      </div>
    );
  }
  // taken
  const human = reason === "reserved" ? "Riservato dal sistema." : reason === "empty" ? "Slug non valido." : "Già usato da un altro agente.";
  return (
    <div className="mt-2 text-[12px] text-[#B14848] flex items-center gap-1.5" data-testid="vetrina-slug-status-taken">
      <WarningCircle size={12} weight="fill" /> {human}
    </div>
  );
}

function PaywallCard({ isReaderMode }) {
  return (
    <div
      className="rounded-2xl border border-[#7C8B6E]/40 bg-gradient-to-br from-[#F1F4ED] to-white p-6 sm:p-8 shadow-sm"
      data-testid="vetrina-paywall"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-[#7C8B6E] mb-3">
        <Lock size={14} weight="fill" />
        <span>Funzione del piano Pro</span>
      </div>
      <h2 className="font-editorial text-2xl sm:text-3xl text-[#1C1C1A] leading-tight mb-3">
        La Vetrina Agente è inclusa nel piano Pro.
      </h2>
      <p className="text-[14.5px] text-[#3F3E3A] leading-relaxed mb-5 max-w-2xl">
        Un sito personale al tuo nome, sempre aggiornato con i tuoi immobili attivi — quello
        che metti nella bio Instagram, nella firma email, sui biglietti da visita.
        Con Pro puoi anche scegliere lo stile, il colore brand e nascondere singoli immobili.
      </p>
      <ul className="text-[13.5px] text-[#3F3E3A] space-y-1.5 mb-6">
        <li className="flex items-start gap-2"><CheckCircle size={14} weight="fill" className="text-[#7C8B6E] mt-0.5" /> 4 stili coerenti con la tua vetrina: Elegante · Lussuoso · Moderno · Minimal</li>
        <li className="flex items-start gap-2"><CheckCircle size={14} weight="fill" className="text-[#7C8B6E] mt-0.5" /> Colore brand personalizzato (palette + HEX)</li>
        <li className="flex items-start gap-2"><CheckCircle size={14} weight="fill" className="text-[#7C8B6E] mt-0.5" /> Aggiornamento automatico ad ogni nuovo annuncio</li>
        <li className="flex items-start gap-2"><CheckCircle size={14} weight="fill" className="text-[#7C8B6E] mt-0.5" /> Toggle &laquo;visibile/nascosta&raquo; per ogni immobile</li>
      </ul>
      <Link
        to={isReaderMode ? "/dashboard/upgrade" : "/pricing"}
        className="btn-primary inline-flex items-center gap-2"
        data-testid="vetrina-paywall-cta"
      >
        Passa al piano Pro <ArrowRight size={14} weight="bold" />
      </Link>
    </div>
  );
}

/* ────────── helpers ────────── */
function ColorPreview({ color }) {
  // Mini-preview: mostra dove il colore appare sulla vetrina pubblica.
  // Un titolo finto + un prezzo + un'icona di accento. Stesso ordine
  // visuale del layout "Elegante" (più rappresentativo).
  return (
    <div className="mt-5 rounded-lg border border-[#E5E1D8] bg-[#FAFAF7] p-4" data-testid="color-preview">
      <div className="text-[9.5px] uppercase tracking-[0.32em] text-[#9A968B] mb-3">
        Anteprima sulla vetrina
      </div>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="text-[10px] uppercase tracking-[0.28em]" style={{ opacity: 0.6 }}>
          In vendita · Milano
        </span>
        <span className="text-[14px] font-mono" style={{ color }}>
          € 420.000
        </span>
      </div>
      <div className="font-editorial italic text-[18px] sm:text-[20px] text-[#1C1C1A] leading-tight mb-1">
        Trilocale luminoso
      </div>
      <div className="text-[12px] text-[#6B6A65] mb-3">
        88 m² · 3 locali · Brera
      </div>
      <span
        className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold"
        style={{ color }}
      >
        Scopri di più →
      </span>
    </div>
  );
}

function FirstSetupGuide() {
  return (
    <div
      className="rounded-2xl border border-[#A8B89A]/40 bg-gradient-to-br from-[#F1F4ED] to-white p-5 sm:p-6 shadow-sm"
      data-testid="vetrina-first-setup-guide"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#5F6B53] mb-2">
        <Lightbulb size={14} weight="fill" />
        <span>Pro · Primo avvio</span>
      </div>
      <h2 className="font-editorial text-[22px] sm:text-[26px] text-[#1C1C1A] leading-tight mb-2">
        Tre campi e sei online.
      </h2>
      <p className="text-[13.5px] text-[#3F3E3A] leading-relaxed max-w-2xl">
        Scegli l&apos;URL, scrivi due righe di bio, premi salva.
        Il sito è pronto.
      </p>
    </div>
  );
}

function normalizeSlug(s) {
  if (!s) return "";
  return s
    .toLowerCase()
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
}
function slugLooksValid(s) {
  const n = normalizeSlug(s);
  return n.length >= 2;
}
